import { useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import Toggle from '@/design_system/Toggle';
import { useUpdateArticle, WARRANTY_OVERRIDE_REASONS } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

export const WarrantyOverrideToggle = () => {
  const { t, i18n } = useLingui();
  const { request, article, view, errors } = useArticleContext();

  const { mutate: updateArticle } = useUpdateArticle({
    articleId: article.id,
    requestId: request.id,
  });

  const overrideReasonOptions = WARRANTY_OVERRIDE_REASONS.map((reason) => ({
    ...reason,
    text: i18n._(reason.text),
  }));

  const selectOverrideReasonLabel = t({
    id: 'article.form.warranty-override-toggle.override-reason.label',
    message: 'Select a reason',
  });

  const hasNoWarrantyComputationFieldsShown = !view.warranty.fields.purchaseDate.shown;
  const warrantyOverrideLabel = hasNoWarrantyComputationFieldsShown
    ? t({
        id: 'article.form.warranty-override-toggle.enable-warranty.label',
        message: 'Enable warranty',
      })
    : t({
        id: 'article.form.warranty-override-toggle.force-warranty-application.label',
        message: 'Force warranty application',
      });

  return (
    <Stack gap="0.5rem">
      <Toggle
        alignment="left"
        isSelected={article.warrantyOverride}
        onChange={(value) => {
          updateArticle({ data: { warrantyOverride: value } });
        }}
        label={warrantyOverrideLabel}
      />

      {article.warrantyOverride && (
        <InputSelect
          aria-label={selectOverrideReasonLabel}
          placeholder={selectOverrideReasonLabel}
          options={overrideReasonOptions}
          value={overrideReasonOptions.find(
            (option) => option.id === article.warrantyOverrideReason
          )}
          getOptionLabel={(option) => option.text}
          getOptionValue={(option) => option.id}
          onChange={(newValue) =>
            updateArticle({ data: { warrantyOverrideReason: newValue?.id ?? null } })
          }
          error={
            errors.warranty?.missingOverrideReason
              ? t({
                  id: 'article.form.warranty-override-toggle.error',
                  message: 'A reason is required',
                })
              : undefined
          }
        />
      )}
    </Stack>
  );
};
